import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

interface Language {
  code: 'en' | 'ar';
  nameEn: string;
  nameAr: string;
  flagUrl: string;
}

const languages: Language[] = [
  { code: 'en', nameEn: 'English', nameAr: 'الإنجليزية', flagUrl: 'https://flagcdn.com/us.svg' },
  { code: 'ar', nameEn: 'Arabic', nameAr: 'العربية', flagUrl: 'https://flagcdn.com/sa.svg' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLanguage = (lng: 'en' | 'ar') => {
    i18n.changeLanguage(lng);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lng', lng);
    navigate({ pathname: location.pathname, search: searchParams.toString() });
    window.location.reload()
    setOpen(false);
  };

  const getLanguageLabel = (code: string) =>
    i18n.language === 'ar'
      ? languages.find((l) => l.code === code)?.nameAr
      : languages.find((l) => l.code === code)?.nameEn;

  const getLanguageFlag = (code: string) =>
    languages.find((l) => l.code === code)?.flagUrl;

  return (
    <div className="relative z-50" ref={dropdownRef}>
      {/* Main Button */}
      <button
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
        onClick={() => setOpen((prev) => !prev)}
      >
        <img src={getLanguageFlag(i18n.language)} alt={getLanguageLabel(i18n.language)} className="w-5 h-5 rounded-sm" />
        <span className=' hidden md:block'>{getLanguageLabel(i18n.language)}</span>
        <ChevronDown size={16} />
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute right-0 mt-2 w-14 md:w-44 bg-white border-gray-200  rounded-xl shadow-xl overflow-hidden transition-all duration-300 ${open ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
          }`}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => switchLanguage(lang.code)}
            className={`flex items-center gap-2 w-full text-left px-4 py-2 transition ${i18n.language === lang.code
                ? 'bg-primary text-white font-semibold'
                : 'hover:bg-primary hover:text-white dark:hover:text-white'
              }`}
          >
            <img src={lang.flagUrl} alt={getLanguageLabel(lang.code)} className="w-5 h-5 rounded-sm" />
            <span className=' hidden md:block'>{getLanguageLabel(lang.code)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
